/**
 * Thin fetch wrapper shared by every screen. Every response is normalised
 * into a discriminated result so a screen never has to special-case a
 * thrown exception (network failure) versus a non-2xx JSON body: "on
 * network error show a retry affordance, never a blank page" applies
 * uniformly by checking `result.kind`.
 *
 * 401-refetch ("every fetch: on 401 response, re-fetch /api/auth/state and
 * re-route", the boot-time session-restore fix class): wired through the
 * single `onUnauthorized` callback below. app.tsx creates ONE ApiClient
 * instance for the whole app and sets onUnauthorized to its
 * reloadAuthState function, so every call site gets the behaviour for
 * free without repeating it.
 *
 * This is safe even for the auth flow's own domain-rejection 401s (wrong
 * code, wrong PIN): re-fetching /api/auth/state while still enrolling or
 * locked returns the same state, so the screen doesn't change underneath
 * the nurse (Preact keeps the same component instance across a same-type
 * re-render, so in-progress local state like a typed PIN or an inline
 * error message is not lost). Each screen still reads the response for its own
 * inline error message independently of this callback — Enrol from the body
 * (invalid_code), Picker from the STATUS ONLY, since board 786/1049 made every
 * pin-login failure return one identical `401 {error:'invalid_pin'}`. The
 * `attemptsLeft` and `409 pin_reset` shapes this comment used to name are gone.
 */

/**
 * ⛔⛔ IS THIS AN ID THIS CLIENT WILL PUT IN A URL? — T1717 M7, security review.
 *
 * ⛔ WHY IT LIVES HERE RATHER THAN ON A SCREEN. There were THREE sites deciding
 * it and they did not agree: `MyResidents`' `residentPath` checked
 * integer-and-positive, its own `409 resident_removed` handler checked only
 * `typeof === 'number'`, and `RequestForm`'s `409` handler carried a third,
 * hand-inlined copy. `NaN`, `1.5` and `-1` are all `typeof 'number'`, and each
 * of them ends up interpolated into `/api/residents/${id}/active`. The M7 deltas
 * fix predicted this ("if a third appears, extract it") and a third had already
 * appeared.
 *
 * ⚠️ A TYPE GUARD, so the callers narrow instead of casting. `unknown` in, so it
 * can be pointed straight at a response body's field.
 * ⚠️ IT IS NOT AN AUTHORISATION CHECK. It says only that the value is a shape
 * this client will spell into a path; the server decides whose row it is, and
 * every roster route is home-scoped from the session.
 */
export function isResidentId(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

export type FetchLike = typeof fetch;

export type ApiResult<T = unknown> = { kind: 'response'; status: number; body: T } | { kind: 'network-error' };

export type ApiClientOptions = {
  /** Injected for tests; defaults to the global fetch in the browser. */
  fetchImpl?: FetchLike;
  /** Called once per response with HTTP status 401, any endpoint. */
  onUnauthorized?: () => void;
  /**
   * ⭐ Board 2706 (plan D5) — called with the `x-portal-build` header of every
   * response that carries one: the client build the server is serving now. The
   * shell (`app.tsx`) compares it with the build this page was served with.
   */
  onBuild?: (id: string) => void;
};

export type ApiClient = {
  get<T = unknown>(path: string, headers?: Record<string, string>): Promise<ApiResult<T>>;
  post<T = unknown>(path: string, body?: unknown, headers?: Record<string, string>): Promise<ApiResult<T>>;
};

export function createApiClient(opts: ApiClientOptions = {}): ApiClient {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const onUnauthorized = opts.onUnauthorized ?? ((): void => {});

  async function request<T>(
    method: string,
    path: string,
    body: unknown,
    headers: Record<string, string> | undefined,
  ): Promise<ApiResult<T>> {
    let res: Response;
    try {
      res = await fetchImpl(path, {
        method,
        credentials: 'same-origin',
        headers: {
          ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
          ...(headers ?? {}),
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    } catch {
      return { kind: 'network-error' };
    }

    let parsedBody: unknown;
    try {
      const text = await res.text();
      parsedBody = text.length > 0 ? JSON.parse(text) : undefined;
    } catch {
      parsedBody = undefined;
    }

    // ⛔ READ DEFENSIVELY: a response with no `headers` (a spec fake is `{ status, text }`)
    // must never throw here, because this path decides what every screen is told.
    const build = typeof res.headers?.get === 'function' ? res.headers.get('x-portal-build') : null;
    if (build) opts.onBuild?.(build);

    if (res.status === 401) {
      onUnauthorized();
    }

    return { kind: 'response', status: res.status, body: parsedBody as T };
  }

  return {
    get: (path, headers) => request('GET', path, undefined, headers),
    post: (path, body, headers) => request('POST', path, body, headers),
  };
}
